import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { styled } from "@pigment-css/react";
import { FilterSelect, MultiSelect, SingleSelect } from "../../../primitives";
import type { FilterSelectGroup } from "../../../primitives";
import { useLocale, useTranslations } from "@acto/web-common/locales";
import { CrossLarge } from "@acto/web-common/components";
import type { FilterDescriptor, FilterParamValueInput, FilterQueryInput, OperatorGroup } from "@acto/gql/graphql";
import { ComparisonOperator, Conjunction, FilterParamType, Timeframe } from "@acto/gql/graphql";
import { useAvailableFilters, useFilterValues } from "@acto/taskpane/core/data-access/workflow";
import { LocalizedStringMap } from "@acto/taskpane/core/domain";

// Product and product-group names can contain commas, so STRING_LIST values are
// flattened into one form-state string with the ASCII Unit Separator (U+001F),
// which never appears in human text.
const MULTI_VALUE_DELIMITER = "\u001F";

interface ExtraParamEntry {
  operator: ComparisonOperator;
  value: string;
}

interface TriggerRuleFormEntry {
  field: string;
  operator: ComparisonOperator;
  value: string;
  timeframe: string;
  extraParams: Record<string, ExtraParamEntry>;
  /** Every param of a rule parsed from a stored workflow, including scope and timeframe. Replayed
   * verbatim when the filter's descriptor is missing, since without one this form cannot type any
   * of them. */
  storedParams?: FilterParamValueInput[];
}

interface TriggerRuleWithKey extends TriggerRuleFormEntry {
  _key: number;
}

interface TriggerRulesSectionProps {
  filterQuery: FilterQueryInput;
  onFilterQueryChange: (query: FilterQueryInput) => void;
}

function findValueParam(filter: FilterDescriptor | undefined) {
  if (!filter) return undefined;

  return filter.parameters.find((p) => p.name === "value");
}

function findTimeframeParam(filter: FilterDescriptor | undefined) {
  if (!filter) return undefined;

  return filter.parameters.find((p) => p.operatorGroups.some((g) => g.valueType === FilterParamType.Timeframe));
}

function getExtraFilterParams(filter: FilterDescriptor | undefined) {
  if (!filter) return [];
  return filter.parameters.filter(
    (p) => p.name !== "value" && !p.operatorGroups.some((g) => g.valueType === FilterParamType.Timeframe),
  );
}

function getDefaultExtraParams(filter: FilterDescriptor | undefined): Record<string, ExtraParamEntry> {
  const result: Record<string, ExtraParamEntry> = {};
  for (const p of getExtraFilterParams(filter)) {
    result[p.name] = {
      operator: p.operatorGroups.flatMap((g) => [...g.operators])[0] ?? ComparisonOperator.Equals,
      value: "",
    };
  }
  return result;
}

function getAllOperators(param: { operatorGroups: readonly OperatorGroup[] } | undefined): ComparisonOperator[] {
  if (!param) return [];

  return param.operatorGroups.flatMap((g) => [...g.operators]);
}

function getValueTypeForOperator(
  param: { operatorGroups: readonly OperatorGroup[] } | undefined,
  operator: ComparisonOperator,
): FilterParamType | undefined {
  if (!param) return undefined;

  for (const group of param.operatorGroups) {
    if (group.operators.includes(operator)) return group.valueType;
  }

  return undefined;
}

export function getParamValueField(valueType: FilterParamType, value: string) {
  switch (valueType) {
    case FilterParamType.Numeric:
      return { numericValue: parseFloat(value) || 0 };
    case FilterParamType.Integer:
      return { intValue: parseInt(value) || 0 };
    case FilterParamType.String:
      return { stringValue: value };
    case FilterParamType.StringList:
      return { stringValues: value ? value.split(MULTI_VALUE_DELIMITER) : [] };
    case FilterParamType.Boolean:
      return { boolValue: value === "true" };
    case FilterParamType.Date:
      return { dateValue: value };
    default:
      return { stringValue: value };
  }
}

export function extractValue(
  param:
    | {
        numericValue?: number | null;
        intValue?: number | null;
        stringValue?: string | null;
        stringValues?: readonly string[] | null;
        boolValue?: boolean | null;
        dateValue?: unknown;
      }
    | undefined,
): string {
  if (!param) return "";
  if (param.numericValue != null) return String(param.numericValue);
  if (param.intValue != null) return String(param.intValue);
  if (param.stringValue != null) return param.stringValue;
  if (param.stringValues?.length) return param.stringValues.join(MULTI_VALUE_DELIMITER);
  if (param.boolValue != null) return String(param.boolValue);
  if (param.dateValue != null) return String(param.dateValue);

  return "";
}

export const buildFilterQuery = (
  rules: TriggerRuleFormEntry[],
  conjunction: Conjunction,
  filtersMap: Map<string, FilterDescriptor>,
): FilterQueryInput => ({
  groupConjunction: conjunction,
  groups: [
    {
      conjunction,
      conditions: rules.map((rule) => {
        const filter = filtersMap.get(rule.field);

        if (!filter) {
          return { filter: rule.field, params: rule.storedParams ?? [] };
        }

        const valueParam = findValueParam(filter);
        const valueType = getValueTypeForOperator(valueParam, rule.operator);

        const params = [
          valueType === undefined
            ? (rule.storedParams?.find((p) => p.name === "value") ?? {
                name: "value",
                operator: rule.operator,
              })
            : {
                name: "value",
                operator: rule.operator,
                ...getParamValueField(valueType, rule.value),
              },
        ];

        for (const [name, entry] of Object.entries(rule.extraParams)) {
          const paramDescriptor = filter.parameters.find((p) => p.name === name);
          const paramValueType = getValueTypeForOperator(paramDescriptor, entry.operator);

          if (paramValueType === undefined) {
            const stored = rule.storedParams?.find((p) => p.name === name);
            if (stored) params.push(stored);
            continue;
          }

          params.push({
            name,
            operator: entry.operator,
            ...getParamValueField(paramValueType, entry.value),
          });
        }

        if (rule.timeframe && findTimeframeParam(filter)) {
          params.push({
            name: "timeframe",
            operator: undefined as unknown as ComparisonOperator,
            stringValue: rule.timeframe,
          });
        }

        return { filter: rule.field, params };
      }),
    },
  ],
});

export const parseFilterQuery = (
  query: FilterQueryInput,
  defaultOperator: ComparisonOperator,
): { rules: TriggerRuleFormEntry[]; conjunction: Conjunction } => {
  const conjunction = query.groupConjunction;
  const rules: TriggerRuleFormEntry[] = [];

  for (const group of query.groups) {
    for (const condition of group.conditions) {
      const valueParam = condition.params.find((p) => p.name === "value");
      const timeframeParam = condition.params.find((p) => p.name === "timeframe");

      const extraParams: Record<string, ExtraParamEntry> = {};
      for (const param of condition.params) {
        if (param.name === "value" || param.name === "timeframe") continue;
        extraParams[param.name] = {
          operator: param.operator ?? ComparisonOperator.Equals,
          value: extractValue(param),
        };
      }

      rules.push({
        field: condition.filter,
        operator: valueParam?.operator ?? defaultOperator,
        value: extractValue(valueParam),
        timeframe: timeframeParam?.stringValue ?? "",
        extraParams,
        storedParams: [...condition.params],
      });
    }
  }

  return { rules, conjunction };
};

const TIMEFRAME_OPTIONS = [
  Timeframe.YearToDate,
  Timeframe.QuarterToDate,
  Timeframe.LastMonth,
  Timeframe.Last_3Months,
  Timeframe.Last_6Months,
  Timeframe.Last_12Months,
  Timeframe.Last_24Months,
];

const Container = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.managementView.spacing[24],
}));

const LogicOperatorWrapper = styled("div")(({ theme }) => ({
  display: "inline-flex",
  alignItems: "center",
  height: "36px",
  backgroundColor: theme.managementView.colors.neutral[0],
  border: `1px solid ${theme.managementView.colors.neutral[8]}`,
  borderRadius: "80px",
  transition: "background-color 0.2s",

  "& button": {
    minHeight: "unset",
    height: "100%",
    borderRight: "none",
    fontSize: theme.managementView.font.size[14],
    color: theme.managementView.colors.neutral[56],
  },

  "&:hover": {
    backgroundColor: theme.managementView.colors.neutral[4],
  },
}));

const TriggerRulesContainer = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.managementView.spacing[12],
}));

const TriggerRuleRow = styled("div")(({ theme }) => ({
  display: "flex",
  gap: theme.managementView.spacing[6],
  minHeight: "48px",
  alignItems: "center",
}));

const TriggerRuleInputs = styled("div")(({ theme }) => ({
  display: "flex",
  flex: "1 1 0",
  minWidth: 0,
  height: "100%",
  alignItems: "center",
  backgroundColor: theme.managementView.colors.neutral[0],
  border: `1px solid ${theme.managementView.colors.neutral[8]}`,
  borderRadius: theme.managementView.radius[16],
  padding: "1px",
  minHeight: "48px",
}));

const TextInput = styled("input")(({ theme }) => ({
  flex: "1 1 auto",
  minWidth: 0,
  height: "100%",
  fontFamily: theme.managementView.font.familySecondary,
  fontSize: theme.managementView.font.size[15],
  fontWeight: theme.managementView.font.weight[440],
  lineHeight: theme.managementView.font.lineHeight[164],
  letterSpacing: theme.managementView.font.letterSpacing.tight,
  color: theme.managementView.colors.neutral[90],
  backgroundColor: "transparent",
  border: "none",
  borderRight: `1px solid ${theme.managementView.colors.neutral[8]}`,
  padding: `0 ${theme.managementView.spacing[16]}`,
  minHeight: "48px",

  "&:focus": {
    outline: "none",
  },

  "&:last-child": {
    borderRight: "none",
  },
}));

const NumberInputWrapper = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.managementView.spacing[8],
  height: "100%",
  borderRight: `1px solid ${theme.managementView.colors.neutral[8]}`,
  padding: `0 ${theme.managementView.spacing[16]} 0 ${theme.managementView.spacing[16]}`,
  width: "96px",
  flexShrink: 0,
  overflow: "hidden",
  minHeight: "48px",
}));

const NumberInput = styled("input")(({ theme }) => ({
  flex: "1 0 0",
  fontFamily: theme.managementView.font.familySecondary,
  fontSize: theme.managementView.font.size[15],
  fontWeight: theme.managementView.font.weight[500],
  lineHeight: theme.managementView.font.lineHeight[164],
  letterSpacing: theme.managementView.font.letterSpacing.tight,
  color: theme.managementView.colors.neutral[56],
  backgroundColor: "transparent",
  border: "none",
  outline: "none",
  textAlign: "left",
  appearance: "textfield",

  "&::-webkit-outer-spin-button, &::-webkit-inner-spin-button": {
    appearance: "none",
    margin: 0,
  },

  "&:focus": {
    color: theme.managementView.colors.neutral[90],
  },
}));

const DeleteButton = styled("button")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: "40px",
  height: "100%",
  backgroundColor: "rgba(153, 0, 13, 0.04)",
  border: "none",
  borderRadius: theme.managementView.radius[16],
  cursor: "pointer",
  transition: "background-color 0.2s",
  minHeight: "48px",

  "&:hover": {
    backgroundColor: "rgba(153, 0, 13, 0.08)",
  },

  "&:focus-visible": {
    outline: `2px solid ${theme.managementView.colors.error[100]}`,
    outlineOffset: "2px",
  },
}));

const LogicSeparator = styled("div")(({ theme }) => ({
  display: "flex",
  gap: theme.managementView.spacing[18],
  alignItems: "center",
  marginTop: theme.managementView.spacing[12],
}));

const Line = styled("div")(({ theme }) => ({
  flex: "1 0 0",
  height: "1px",
  backgroundColor: theme.managementView.colors.neutral[8],
}));

const AddTriggerButton = styled("button")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.managementView.spacing[8],
  height: "48px",
  padding: `0 ${theme.managementView.spacing[16]}`,
  backgroundColor: theme.managementView.colors.neutral[8],
  border: "none",
  borderRadius: theme.managementView.radius[16],
  fontFamily: theme.managementView.font.familySecondary,
  fontSize: theme.managementView.font.size[15],
  fontWeight: theme.managementView.font.weight[500],
  lineHeight: theme.managementView.font.lineHeight[164],
  letterSpacing: theme.managementView.font.letterSpacing.tight,
  color: theme.managementView.colors.neutral[90],
  cursor: "pointer",
  width: "fit-content",
  transition: "background-color 0.2s",

  "&:hover": {
    backgroundColor: theme.managementView.colors.neutral[24],
  },

  "&:focus-visible": {
    outline: `2px solid ${theme.colors.brand[500]}`,
    outlineOffset: "2px",
  },
}));

interface OperatorDropdownProps {
  logicOperator: Conjunction;
  onOperatorChange: (value: string) => void;
  andLabel: string;
  orLabel: string;
}

const OperatorDropdown = ({ logicOperator, onOperatorChange, andLabel, orLabel }: OperatorDropdownProps) => {
  const options = [
    { key: Conjunction.And, label: andLabel },
    { key: Conjunction.Or, label: orLabel },
  ];

  return (
    <LogicOperatorWrapper>
      <SingleSelect options={options} value={logicOperator} onChange={onOperatorChange} />
    </LogicOperatorWrapper>
  );
};

interface FilterValueInputProps {
  valueType: FilterParamType | undefined;
  value: string;
  filterName: string;
  operator: ComparisonOperator;
  onChange: (value: string) => void;
}

const FREETEXT_OPERATORS = new Set([ComparisonOperator.StartsWith]);

const FilterValueInput = ({ valueType, value, filterName, operator, onChange }: FilterValueInputProps) => {
  const t = useTranslations("managementView.workflow.createWorkflow");
  const { locale } = useLocale();

  const isFreeText = FREETEXT_OPERATORS.has(operator);
  const shouldFetchValues =
    !isFreeText && (valueType === FilterParamType.String || valueType === FilterParamType.StringList);
  const { values, isPending: isLoadingValues } = useFilterValues(shouldFetchValues ? filterName : null);

  if (valueType === undefined) {
    return <TextInput type="text" value={value} disabled />;
  }

  if (isFreeText) {
    return <TextInput type="text" value={value} onChange={(e) => onChange(e.target.value)} />;
  }

  const selectedValues = value ? value.split(MULTI_VALUE_DELIMITER) : [];

  switch (valueType) {
    case FilterParamType.StringList:
      return (
        <MultiSelect
          options={values.map((v) => ({
            key: v.key,
            label: LocalizedStringMap.fromGraphql(v.label).getTranslation(locale),
          }))}
          selected={selectedValues}
          disabled={isLoadingValues}
          searchPlaceholder={t("valueSearchPlaceholder", { defaultValue: "Search.." })}
          noResultsText={t("valueSearchNoResults", { defaultValue: "No results" })}
          onChange={(selected) => onChange(selected.join(MULTI_VALUE_DELIMITER))}
        />
      );

    case FilterParamType.String:
      return (
        <SingleSelect
          options={values.map((v) => ({
            key: v.key,
            label: LocalizedStringMap.fromGraphql(v.label).getTranslation(locale),
          }))}
          value={value}
          disabled={isLoadingValues}
          searchPlaceholder={t("valueSearchPlaceholder", { defaultValue: "Search.." })}
          noResultsText={t("valueSearchNoResults", { defaultValue: "No results" })}
          onChange={onChange}
        />
      );

    case FilterParamType.Boolean:
      return (
        <SingleSelect
          options={[
            { key: "true", label: t("boolTrue", { defaultValue: "Yes" }) },
            { key: "false", label: t("boolFalse", { defaultValue: "No" }) },
          ]}
          value={value}
          onChange={onChange}
        />
      );

    case FilterParamType.Date:
      return <TextInput type="date" value={value} onChange={(e) => onChange(e.target.value)} />;

    case FilterParamType.Numeric:
    case FilterParamType.Integer:
    default:
      return (
        <NumberInputWrapper>
          <NumberInput
            type="number"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            min="0"
            step={valueType === FilterParamType.Integer ? "1" : "any"}
          />
        </NumberInputWrapper>
      );
  }
};

const CATEGORY_ORDER = ["customer", "orders", "product", "productGroup", "interactions", "offer", "signal"] as const;

function groupFiltersByCategory(
  filters: readonly FilterDescriptor[],
): readonly (readonly [string, FilterDescriptor[]])[] {
  const groups = new Map<string, FilterDescriptor[]>();

  for (const filter of filters) {
    const category = filter.category;
    const group = groups.get(category);

    if (group) {
      group.push(filter);
    } else {
      groups.set(category, [filter]);
    }
  }

  const knownCategories = new Set<string>(CATEGORY_ORDER);
  const ordered: (readonly [string, FilterDescriptor[]])[] = CATEGORY_ORDER.flatMap((cat) => {
    const categoryFilters = groups.get(cat);
    return categoryFilters ? ([[cat, categoryFilters]] as const) : [];
  });

  for (const [cat, categoryFilters] of groups) {
    if (!knownCategories.has(cat)) {
      ordered.push([cat, categoryFilters]);
    }
  }

  return ordered;
}

export const TriggerRulesSection = ({ filterQuery, onFilterQueryChange }: TriggerRulesSectionProps) => {
  const t = useTranslations("managementView.workflow.createWorkflow");
  const { locale } = useLocale();
  const { filters } = useAvailableFilters();

  const filtersMap = useMemo(() => new Map(filters.map((f) => [f.name, f])), [filters]);

  const filterSelectGroups: FilterSelectGroup[] = groupFiltersByCategory(filters).map(
    ([category, categoryFilters]) => ({
      key: category,
      label: t(`filterCategories.${category}`, { defaultValue: category }),
      options: categoryFilters.map((f) => ({
        key: f.name,
        label: LocalizedStringMap.fromGraphql(f.label).getTranslation(locale),
      })),
    }),
  );

  const defaultField = filters[0]?.name ?? "";
  const defaultOperator = useMemo(() => {
    const first = filters[0];
    if (!first) return ComparisonOperator.Equals;

    const operators = getAllOperators(findValueParam(first));
    return operators[0] ?? ComparisonOperator.Equals;
  }, [filters]);

  const { rules: initialRules, conjunction: initialConjunction } = parseFilterQuery(filterQuery, defaultOperator);
  const [conjunction, setConjunction] = useState<Conjunction>(initialConjunction);

  useEffect(() => {
    setConjunction(filterQuery.groupConjunction);
  }, [filterQuery.groupConjunction]);

  const nextKeyRef = useRef(1);

  const rulesWithKeys = useRef<TriggerRuleWithKey[]>(initialRules.map((rule, i) => ({ ...rule, _key: i })));
  if (initialRules.length > 0) {
    nextKeyRef.current = Math.max(nextKeyRef.current, initialRules.length);
  }

  const syncKeys = (rules: TriggerRuleFormEntry[]): TriggerRuleWithKey[] => {
    return rules.map((rule, i) => {
      const existing = rulesWithKeys.current[i];
      if (
        existing &&
        existing.field === rule.field &&
        existing.operator === rule.operator &&
        existing.value === rule.value &&
        existing.timeframe === rule.timeframe &&
        JSON.stringify(existing.extraParams) === JSON.stringify(rule.extraParams)
      ) {
        return existing;
      }
      return { ...rule, _key: existing?._key ?? nextKeyRef.current++ };
    });
  };

  const currentRules = syncKeys(initialRules);

  useEffect(() => {
    rulesWithKeys.current = currentRules;
  });

  const emitChange = useCallback(
    (rules: TriggerRuleFormEntry[], conj: Conjunction) => {
      onFilterQueryChange(buildFilterQuery(rules, conj, filtersMap));
    },
    [onFilterQueryChange, filtersMap],
  );

  const handleConjunctionChange = useCallback(
    (value: string) => {
      const newConj = value as Conjunction;
      setConjunction(newConj);
      emitChange(initialRules, newConj);
    },
    [initialRules, emitChange],
  );

  const handleAddTrigger = () => {
    const filter = filtersMap.get(defaultField);
    const newRule: TriggerRuleFormEntry = {
      field: defaultField,
      operator: defaultOperator,
      value: "",
      timeframe: "",
      extraParams: getDefaultExtraParams(filter),
    };
    emitChange([...initialRules, newRule], conjunction);
  };

  const handleRemoveTrigger = (index: number) => {
    rulesWithKeys.current = rulesWithKeys.current.filter((_, i) => i !== index);
    emitChange(
      initialRules.filter((_, i) => i !== index),
      conjunction,
    );
  };

  const handleUpdateRule = (index: number, updates: Partial<TriggerRuleFormEntry>) => {
    const newRules = [...initialRules];
    newRules[index] = { ...newRules[index], ...updates };

    if (updates.field && updates.field !== initialRules[index].field) {
      const newFilter = filtersMap.get(updates.field);
      const operators = getAllOperators(findValueParam(newFilter));
      newRules[index].operator = operators[0] ?? ComparisonOperator.Equals;
      newRules[index].value = "";
      newRules[index].timeframe = "";
      newRules[index].extraParams = getDefaultExtraParams(newFilter);
    }

    if (updates.operator && updates.operator !== initialRules[index].operator) {
      const filter = filtersMap.get(newRules[index].field);
      const valueParam = findValueParam(filter);
      const oldType = getValueTypeForOperator(valueParam, initialRules[index].operator);
      const newType = getValueTypeForOperator(valueParam, updates.operator);
      if (oldType !== newType) {
        newRules[index].value = "";
      }
    }

    rulesWithKeys.current[index] = { ...rulesWithKeys.current[index], ...newRules[index] };
    emitChange(newRules, conjunction);
  };

  const handleUpdateExtraParam = (ruleIndex: number, paramName: string, value: string) => {
    const newRules = [...initialRules];
    newRules[ruleIndex] = {
      ...newRules[ruleIndex],
      extraParams: {
        ...newRules[ruleIndex].extraParams,
        [paramName]: { ...newRules[ruleIndex].extraParams[paramName], value },
      },
    };
    rulesWithKeys.current[ruleIndex] = { ...rulesWithKeys.current[ruleIndex], ...newRules[ruleIndex] };
    emitChange(newRules, conjunction);
  };

  return (
    <Container>
      <TriggerRulesContainer>
        {currentRules.map((rule, index) => {
          const filter = filtersMap.get(rule.field);
          const valueParam = findValueParam(filter);
          const operators = getAllOperators(valueParam);
          const valueType = getValueTypeForOperator(valueParam, rule.operator);
          const hasTimeframe = !!findTimeframeParam(filter);

          return (
            <div key={rule._key}>
              <TriggerRuleRow>
                <TriggerRuleInputs>
                  <FilterSelect
                    groups={filterSelectGroups}
                    value={rule.field}
                    searchPlaceholder={t("filterSearchPlaceholder", { defaultValue: "Filter trigger.." })}
                    onChange={(v) => handleUpdateRule(index, { field: v })}
                  />

                  <SingleSelect
                    options={operators.map((op) => ({
                      key: op,
                      label: t(`operators.${op}`, { defaultValue: op }),
                    }))}
                    value={rule.operator}
                    onChange={(v) => handleUpdateRule(index, { operator: v as ComparisonOperator })}
                  />

                  <FilterValueInput
                    valueType={valueType}
                    value={rule.value}
                    filterName={rule.field}
                    operator={rule.operator}
                    onChange={(v) => handleUpdateRule(index, { value: v })}
                  />

                  {getExtraFilterParams(filter).map((extraParamDef) => {
                    const extraParam = rule.extraParams[extraParamDef.name];
                    if (!extraParam) return null;
                    const paramValueType = getValueTypeForOperator(extraParamDef, extraParam.operator);
                    return (
                      <FilterValueInput
                        key={extraParamDef.name}
                        valueType={paramValueType}
                        value={extraParam.value}
                        filterName={rule.field}
                        operator={extraParam.operator}
                        onChange={(v) => handleUpdateExtraParam(index, extraParamDef.name, v)}
                      />
                    );
                  })}

                  {hasTimeframe && (
                    <SingleSelect
                      options={TIMEFRAME_OPTIONS.map((tf) => ({
                        key: tf,
                        label: t(`timeframes.${tf}`, { defaultValue: tf }),
                      }))}
                      value={rule.timeframe}
                      onChange={(v) => handleUpdateRule(index, { timeframe: v })}
                    />
                  )}
                </TriggerRuleInputs>

                <DeleteButton type="button" onClick={() => handleRemoveTrigger(index)} aria-label={t("removeTrigger")}>
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <CrossLarge color="#99000D" />
                  </svg>
                </DeleteButton>
              </TriggerRuleRow>

              {index < currentRules.length - 1 && (
                <LogicSeparator>
                  <OperatorDropdown
                    logicOperator={conjunction}
                    onOperatorChange={handleConjunctionChange}
                    andLabel={t("andOperator")}
                    orLabel={t("orOperator")}
                  />
                  <Line />
                </LogicSeparator>
              )}
            </div>
          );
        })}
      </TriggerRulesContainer>

      <AddTriggerButton type="button" onClick={handleAddTrigger}>
        {t("addTrigger")}
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
          <path
            d="M8 3.5V12.5M3.5 8H12.5"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </AddTriggerButton>
    </Container>
  );
};
